import React, { useState } from 'react';
import { ReactEditor } from 'slate-react';
import styled, { css } from 'styled-components';
import DropdownListContainer from '~/components/Dropdown/components/DropdownListContainer';
import Icon, { IconType } from '~/components/Icon';
import { Option } from '~/components/Inputs/Dropdown';
import { DHEditor } from '~/components/PluginsEditor/types';
import BaseButton from '../Base';

export type Props = {
  dataTestId?: string;
  options?: Array<Option>;
  icon: IconType;
  selectedOption?: string | number;
  tooltipMessage?: string;
  editor: DHEditor;
  /**
   * If you want to keep the selection in the editor when you click the options button.
   * Must be false in hovering toolbars otherwise dropdown list cannot be clicked
   * */
  persistFocus: boolean;
};

const OptionsButton: React.FC<Props> = ({
  dataTestId,
  icon,
  options = [],
  selectedOption,
  tooltipMessage,
  persistFocus = false,
  editor,
}) => {
  const [showOptions, setShowOptions] = useState(false);

  return (
    <Container>
      <BaseButton
        icon={icon}
        onClick={() => {
          setShowOptions(true);
          if (persistFocus) ReactEditor.focus(editor);
        }}
        active={showOptions}
        tooltipMessage={showOptions ? '' : tooltipMessage}
        dataTestId={dataTestId}
      >
        <Icon name="chevron" flipX={showOptions} strokeWidth={2.5} />
      </BaseButton>

      <DropdownListContainer
        options={options}
        dropdownListOpen={showOptions}
        selectedOptionIdx={
          options?.findIndex(option => option.payload === selectedOption) || 0
        }
        onChange={({ option }) => {
          if (option.onClickAction) {
            option.onClickAction(option.payload);
          }

          setShowOptions(false);
        }}
        onClickOutside={() => setShowOptions(false)}
        onClose={() => setShowOptions(false)}
        loading={false}
      />
    </Container>
  );
};

const Container = styled.div<{}>`
  position: relative;
`;

export const OptionLabel = styled.div<{}>(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;

    /* Pass margin to icons on the left in the options button */
    & > :first-child {
      margin-right: ${theme.space('xxs')};
    }
  `,
);

export default OptionsButton;
